import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in Leaflet with Vite
// https://github.com/Leaflet/Leaflet/issues/4968
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface Coordinates {
  latitude: number | null;
  longitude: number | null;
}

interface CoordinateSelectorProps {
  latitude: number | null;
  longitude: number | null;
  onChange: (coords: Coordinates) => void;
  label: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
}

// Component to handle map clicks
const MapClickHandler: React.FC<{
  onClick: (lat: number, lng: number) => void;
  disabled?: boolean;
}> = ({ onClick, disabled }) => {
  useMapEvents({
    click: (e) => {
      if (!disabled) {
        onClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
};

// Component to recenter map when coordinates change externally
const MapCenterUpdater: React.FC<{
  center: [number, number];
}> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (center[0] !== 0 || center[1] !== 0) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);

  return null;
};

export const CoordinateSelector: React.FC<CoordinateSelectorProps> = ({
  latitude,
  longitude,
  onChange,
  label,
  error,
  hint,
  disabled = false,
}) => {
  const { t } = useTranslation();

  // Local state for input fields
  const [latInput, setLatInput] = useState<string>(latitude?.toString() || '');
  const [lngInput, setLngInput] = useState<string>(longitude?.toString() || '');
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  // Default center (Poland)
  const defaultCenter: [number, number] = [52.2297, 21.0122]; // Warsaw

  // Current marker position
  const markerPosition: [number, number] | null =
    latitude != null && longitude != null
      ? [latitude, longitude]
      : null;

  // Map center
  const mapCenter: [number, number] = markerPosition || defaultCenter;

  // Sync local inputs with props
  useEffect(() => {
    setLatInput(latitude?.toString() || '');
    setLngInput(longitude?.toString() || '');
  }, [latitude, longitude]);

  const handleMapClick = useCallback((lat: number, lng: number) => {
    const roundedLat = Math.round(lat * 1000000) / 1000000;
    const roundedLng = Math.round(lng * 1000000) / 1000000;
    onChange({ latitude: roundedLat, longitude: roundedLng });
  }, [onChange]);

  const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLatInput(value);

    // Allow empty value (clears coordinate)
    if (value.trim() === '') {
      onChange({ latitude: null, longitude });
      return;
    }

    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed >= -90 && parsed <= 90) {
      onChange({ latitude: parsed, longitude });
    }
  };

  const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLngInput(value);

    // Allow empty value (clears coordinate)
    if (value.trim() === '') {
      onChange({ latitude, longitude: null });
      return;
    }

    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed >= -180 && parsed <= 180) {
      onChange({ latitude, longitude: parsed });
    }
  };

  const handleLatBlur = () => {
    // If empty, keep it empty (null)
    if (latInput.trim() === '') {
      return;
    }
    const parsed = parseFloat(latInput);
    if (isNaN(parsed) || parsed < -90 || parsed > 90) {
      setLatInput(latitude?.toString() || '');
    }
  };

  const handleLngBlur = () => {
    // If empty, keep it empty (null)
    if (lngInput.trim() === '') {
      return;
    }
    const parsed = parseFloat(lngInput);
    if (isNaN(parsed) || parsed < -180 || parsed > 180) {
      setLngInput(longitude?.toString() || '');
    }
  };

  const clearCoordinates = () => {
    setLatInput('');
    setLngInput('');
    onChange({ latitude: null, longitude: null });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-ink-primary">
          {label}
        </label>
        <button
          type="button"
          onClick={() => setIsMapExpanded(!isMapExpanded)}
          className="text-sm text-brand-primary hover:text-brand-primary transition-colors"
        >
          {isMapExpanded ? t('businessProfile.hideMap') : t('businessProfile.showMap')}
        </button>
      </div>

      {/* Coordinate inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-ink-muted mb-1">
            {t('businessProfile.latitude')}
          </label>
          <input
            type="number"
            step="0.000001"
            min="-90"
            max="90"
            value={latInput}
            onChange={handleLatChange}
            onBlur={handleLatBlur}
            disabled={disabled}
            placeholder="52.229675"
            className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 transition-colors text-sm ${
              error
                ? 'border-brand-warm focus:ring-red-500/50'
                : 'border-border-strong focus:ring-brand-primary/50 focus:border-brand-primary'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
        </div>
        <div>
          <label className="block text-xs text-ink-muted mb-1">
            {t('businessProfile.longitude')}
          </label>
          <input
            type="number"
            step="0.000001"
            min="-180"
            max="180"
            value={lngInput}
            onChange={handleLngChange}
            onBlur={handleLngBlur}
            disabled={disabled}
            placeholder="21.012230"
            className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 transition-colors text-sm ${
              error
                ? 'border-brand-warm focus:ring-red-500/50'
                : 'border-border-strong focus:ring-brand-primary/50 focus:border-brand-primary'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
        </div>
      </div>

      {/* Clear button */}
      {(latitude != null || longitude != null) && !disabled && (
        <button
          type="button"
          onClick={clearCoordinates}
          className="text-sm text-ink-secondary hover:text-ink-primary transition-colors"
        >
          {t('businessProfile.clearCoordinates')}
        </button>
      )}

      {/* Map */}
      {isMapExpanded && (
        <div className="relative rounded-lg overflow-hidden border border-border-strong">
          <MapContainer
            center={mapCenter}
            zoom={markerPosition ? 15 : 6}
            style={{ height: 'clamp(200px, 40vh, 350px)', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onClick={handleMapClick} disabled={disabled} />
            <MapCenterUpdater center={mapCenter} />
            {markerPosition && (
              <Marker position={markerPosition} />
            )}
          </MapContainer>

          {/* Map instructions */}
          <div className="absolute bottom-2 left-2 right-2 bg-surface-card/95 backdrop-blur-sm rounded px-3 py-2 text-xs text-ink-primary pointer-events-none">
            {disabled
              ? t('businessProfile.mapDisabled')
              : t('businessProfile.mapInstructions')
            }
          </div>
        </div>
      )}

      {/* Hint */}
      {hint && !error && (
        <p className="text-sm text-ink-muted">{hint}</p>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-brand-warm-deep">{error}</p>
      )}
    </div>
  );
};
